import axios, { AxiosError, AxiosResponse } from 'axios';
import { toast } from 'sonner';
import { env, ensureHttps } from '@/config/environment';
import { TokenManager } from '@/lib/token-manager';

/**
 * The shared axios instance every `*-api.ts` module builds on: cookie auth,
 * CSRF, the empty-body fix, proactive token refresh and the global error toasts.
 */

// Ensure API base URL uses HTTPS in production
const secureApiBaseUrl = ensureHttps(env.apiBaseUrl);

// Create axios instance with secure configuration
const apiBase = axios.create({
  baseURL: secureApiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  // Django CSRF protection
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  // Allow credentials for authentication
  withCredentials: true,
  timeout: 10000, // 10 second timeout to prevent hanging requests
});

/**
 * Token refresh
 *
 * Backend contract (POST /api/v1/auth/token/refresh/, IsAuthenticated):
 *   - Authenticates via the auth cookie (or Token header).
 *   - Slides the expiry window and re-sets the cookie; the key is NOT rotated, so
 *     the call is idempotent and other devices keep their session.
 *   - Empty request body: there is no separate refresh-token credential.
 *
 * Tokens expire after 1h of inactivity, so we refresh proactively when a request
 * fires within REFRESH_THRESHOLD_MS of expiry. A 401 means refresh is no longer
 * possible: fall through to redirect.
 */
const TOKEN_TTL_MS = 60 * 60 * 1000;        // matches backend TOKEN_EXPIRED_AFTER_SECONDS=3600
const REFRESH_THRESHOLD_MS = 15 * 60 * 1000; // refresh if < 15 min remaining

let inflightRefresh: Promise<void> | null = null;

async function refreshAuthSession(): Promise<void> {
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async () => {
    await axios.post(
      `${secureApiBaseUrl}/auth/token/refresh/`,
      {},
      {
        headers: { 'X-Use-Cookie-Auth': 'true' },
        withCredentials: true,
        timeout: 8000,
      }
    );
    TokenManager.updateToken(undefined, TOKEN_TTL_MS);
  })().finally(() => {
    inflightRefresh = null;
  });

  return inflightRefresh;
}

apiBase.interceptors.request.use(
  async (config) => {
    // Add environment-specific headers regardless of auth state
    if (env.isDevelopment) config.headers['X-Development'] = 'true';
    if (env.isProduction) config.headers['X-Client-Version'] = '1.0.0';

    const hasSession = TokenManager.isTokenValid();
    const expiring = TokenManager.isTokenExpiring(REFRESH_THRESHOLD_MS);

    config.headers['X-Use-Cookie-Auth'] = 'true';

    /* The API's validation middleware rejects any POST/PUT/PATCH without a
       Content-Type. Axios omits the header entirely when there is no body,
       which silently broke every bodyless action call (club join/leave,
       challenge join/leave, review approve, 2FA setup). Send an empty JSON
       object for those. FormData is left alone so axios keeps its
       multipart boundary. */
    const method = (config.method || 'get').toUpperCase();
    if (['POST', 'PUT', 'PATCH'].includes(method)) {
      const isFormData =
        typeof FormData !== 'undefined' && config.data instanceof FormData;
      if (!isFormData && (config.data === undefined || config.data === null)) {
        config.data = {};
        config.headers['Content-Type'] = 'application/json';
      }
    }

    if (hasSession && expiring) {
      try {
        await refreshAuthSession();
      } catch {
        // Best-effort; the response interceptor handles stale cookies.
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Every /api/v1/ error arrives as the standard envelope
 * `{ error: { code, message, details?, request_id } }` (backend `core/errors.py`).
 * `details` carries field-level messages (`{ field: [msg] }`) for forms.
 */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    request_id?: string;
    details?: Record<string, string[] | string>;
  };
}

export const getApiError = (error: unknown): ApiErrorBody['error'] | null => {
  const data = (error as AxiosError<ApiErrorBody> | undefined)?.response?.data;
  return data && typeof data === 'object' && 'error' in data && data.error ? data.error : null;
};

/** Field errors keyed by field name, ready for `setError` in forms. */
export const getFieldErrors = (error: unknown): Record<string, string> => {
  const details = getApiError(error)?.details;
  if (!details) return {};
  return Object.fromEntries(
    Object.entries(details).map(([field, value]) => [field, Array.isArray(value) ? value[0] : String(value)])
  );
};

/**
 * Attach an `Idempotency-Key` so a retried POST (payment, quick issue/return)
 * replays the stored result instead of acting twice. Call once per user action and
 * reuse the key for that action's retries.
 */
export const idempotencyHeaders = (key: string = crypto.randomUUID()) => ({
  'Idempotency-Key': key,
});

/**
 * Response interceptor: one toast per failure, driven by the envelope.
 * 401 is the only status that ends the session; everything else is reported and
 * left to the caller.
 */
apiBase.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const apiError = getApiError(error);

    const getMessage = (): string => {
      if (!apiError) return error.message || 'An unexpected error occurred';
      const fieldErrors = apiError.details
        ? Object.entries(apiError.details)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value[0] : value}`)
            .join(', ')
        : '';
      return fieldErrors ? `${apiError.message} (${fieldErrors})` : apiError.message;
    };

    const message = getMessage();

    switch (status) {
      case 400:
        toast.error(message);
        break;

      case 401:
        toast.error('Your session has expired. Please log in again.');
        TokenManager.clearTokens();
        // Use timeout to allow toast to show before redirect
        setTimeout(() => {
          window.location.href = '/signin';
        }, 1500);
        break;

      case 403:
        toast.error(apiError?.message || 'You do not have permission to perform this action.');
        break;

      case 404:
        toast.error(apiError?.message || 'The requested resource was not found.');
        break;

      case 409:
        toast.error(`Conflict: ${message}`);
        break;

      case 429:
        toast.error('Too many requests. Please slow down and try again later.');
        break;

      case 500:
        toast.error(
          apiError?.request_id
            ? `Server error (ref ${apiError.request_id.slice(0, 8)}). Quote that reference if it keeps happening.`
            : 'Server error. Try again in a moment.'
        );
        if (env.sentryDsn) {
          console.error('Server error:', error);
        }
        break;

      case 502:
      case 503:
      case 504:
        toast.error('Service temporarily unavailable. Please try again in a moment.');
        break;

      default:
        // No status means no answer at all (offline, DNS, the 10 s timeout); there is
        // no server wording to show, so say what happened in plain words.
        toast.error(
          error.response
            ? message || 'An unexpected error occurred'
            : 'The request did not reach the server. Check the connection and try again.'
        );
    }

    return Promise.reject(error);
  }
);

/**
 * Common API response types for type safety across modules
 */
export interface PaginatedResponse<T> {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: number;
}

/**
 * Common query parameters for filtering and pagination
 */
export interface BaseQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
}

/**
 * Export the configured axios instance for use in module-specific APIs
 */
export default apiBase;

// Unauthenticated axios instance for public endpoints (registration, etc.)
export const publicApi = axios.create({
  baseURL: secureApiBaseUrl,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false,
  timeout: 10000,
});
